import { inject, Injectable, Injector, OnInit, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, Subject, tap } from 'rxjs';
import { JwtService } from './jwt.service';
import { Iuser, IUserProfileSummary } from '../shared/user';
import { Utilities } from '../../core/utilis/Utilities';
import { IUserProfile } from '../shared/model/userProfile';
import { PermissionService } from '../../shared/services/permission.service';
import { ConfigService } from '../../core/config/config.service';
import { UaePassAuthService } from './uae-pass-auth.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnInit {
  private http = inject(HttpClient);
  private injector = inject(Injector);
  private jwtService = inject(JwtService);
  private router = inject(Router);
  public isInLogin = signal(false);
  user$ = signal<Iuser | null>(null);
  profilePictureUrl: Subject<string> = new Subject();

  constructor(
    private config: ConfigService,
    private permissionService: PermissionService
  ) {
    if (localStorage.getItem('token')) {
      this.jwtService.setToken(localStorage.getItem('token'));
      this.user$.set(this.jwtService.getUser());
    }
  }

  ngOnInit(): void {
    this.injector.get(UaePassAuthService).user$.subscribe((user) => {
      if (user) {
        this.user$.set(user);
      }
    });
  }

  public setFileURL(fileName: string): Observable<any> {
    return this.http.post(this.config.apiUrl + '/FileService/api/File/Urls', [
      fileName
    ]);
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('token');
  }

  getUser() {
    return this.http.get<IUserProfileSummary>(
      this.config.apiUrl + '/UserManagement/api/Profile/Get/CurrentUser'
    );
  }

  getUserToken(): string | null {
    return localStorage.getItem('token');
  }

  getPermissions() {
    return this.jwtService?.getUser()?.permissions;
  }

  getUserJourney(): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'text/plain');
    const requestOptions: Object = { headers: headers, responseType: 'text' };

    return this.http.get<any>(
      this.config.apiUrl + '/Backend/api/Preferences/GetUserJourney',
      requestOptions
    );
  }

  getCurrentUserProfile(): Observable<IUserProfile> {
    return this.http.get<any>(
      this.config.apiUrl + '/UserManagement/api/Profile/Get/CurrentUser'
    );
  }

  setPreference(req?: any): Observable<any> {
    const param = Utilities.createRequestOption(req);

    return this.http.put(
      this.config.apiUrl + '/Backend/api/Preferences/SetUserJourney',
      {},
      { params: param }
    );
  }

  register(body: any): Observable<any> {
    return this.http.post(
      this.config.apiUrl + '/Identity/api/Identity/Register',
      body
    );
  }

  login(body: { userName: string; password: string }): Observable<Iuser> {
    const header = new HttpHeaders().set('scope', 'Portal');

    return this.http
      .post<Iuser>(this.config.apiUrl + '/Identity/api/Identity/Login', body, {
        headers: header
      })
      .pipe(
        tap((res: Iuser) => {
          this.jwtService.setToken(res.accessToken);
          this.user$.set(this.jwtService.getUser());
        })
      );
  }

  logout(): Observable<any> {
    Object.keys(localStorage).forEach(
      (key) => key !== 'lang' && localStorage.removeItem(key)
    );

    this.permissionService.clearPermissions();
    const isUaePassUser = !!!this.user$()?.IsUaePassUser;

    if (isUaePassUser) {
      return this.injector.get(UaePassAuthService).logoutFromUaePass();
    }

    return of(null).pipe(
      tap(() => {
        setTimeout(() => {
          this.router.navigate(['/auth/login']);
        });
      })
    );
  }

  refreshToken(body: any): Observable<any> {
    return this.http
      .post<any>(
        this.config.apiUrl + '/Identity/api/Identity/RefreshToken',
        body
      )
      .pipe(
        tap((res: Iuser) => {
          this.jwtService.setToken(res.accessToken);
          this.user$.set(this.jwtService.getUser());
        })
      );
  }

  sendOtp(body: { email: string }): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };

    return this.http.post(
      this.config.apiUrl + '/Identity/api/Otp/Send',
      body,
      httpOptions
    );
  }

  validateOtp(payload: { otp: string; email: string }): Observable<any> {
    return this.http.post(
      this.config.apiUrl + '/Identity/api/Otp/Validate',
      payload
    );
  }

  updateEmail(payload: { otp: string; newEmail: string }): Observable<any> {
    return this.http.put(
      this.config.apiUrl + '/UserManagement/api/User/Email/Update',
      payload
    );
  }

  changePassword(body: any): Observable<any> {
    return this.http.post(
      this.config.apiUrl + '/Identity/api/Identity/Password/Reset',
      body
    );
  }

  updateUserProfile(body: any) {
    return this.http.put(
      this.config.apiUrl + '/UserManagement/api/Profile/Update',
      body
    );
  }

  uploadUserProfile(body: any) {
    return this.http.put(
      this.config.apiUrl + '/UserManagement/api/User/UpdateProfilePicture',
      body
    );
  }
}
